import jsPDF from "jspdf";
import html2canvas from "html2canvas";
import JSZip from "jszip";
import { getDateInExportFormat } from "../../../store";
import { exportRegistry, getExportMeta } from "../exportRegistry";
import { formatYYMMDDHHMM } from "../index.js";

const orgLogoDataURL = localStorage.getItem("cachedImage");

// minimal one-time font embed
let exo2Ready = false;

async function ensureExo2(pdf) {
  if (exo2Ready) return;

  const base = (process.env.PUBLIC_URL || "/").replace(/\/?$/, "/");
  const toB64 = async (url) => {
    const r = await fetch(url);
    if (!r.ok) throw new Error(`Font load failed: ${url}`);
    const buf = await r.arrayBuffer();
    let s = "",
      u = new Uint8Array(buf),
      CH = 0x8000;
    for (let i = 0; i < u.length; i += CH)
      s += String.fromCharCode.apply(null, u.subarray(i, i + CH));
    return btoa(s);
  };

  const [regB64, boldB64] = await Promise.all([
    toB64(`${base}fonts/Exo2-Regular.ttf`),
    toB64(`${base}fonts/Exo2-Bold.ttf`),
  ]);

  pdf.addFileToVFS("Exo2-Regular.ttf", regB64);
  pdf.addFont("Exo2-Regular.ttf", "Exo2", "normal");

  pdf.addFileToVFS("Exo2-Bold.ttf", boldB64);
  pdf.addFont("Exo2-Bold.ttf", "Exo2", "bold");

  exo2Ready = true;
}

// ---- existing map ----
export const moduleIDs = {
  Documenter_Documentation_Status: "documenter_dept_status",
  Remediator_Department_vs_Compliance_Status:
    "remediator_department_wise_status",
  Remediator_Priority_vs_Compliance_Status: "remediator_priority_wise_status",
  Remediator_Department_vs_Priority_Status:
    "remediator_department_wise_priority_status",
  Auditor_Audit_Status: "audit_status",
  Auditor_overview: "auditor_overview",
  Educator_Assessment_vs_Status: "educator_assessment_status",
  Educator_Department_vs_Status: "educator_department_status",
  Educator_Overall_Coverage: "educator_overall_coverage",
  Investigator_Severity_vs_Incident_Status: "investigator_severity_status",
  Investigator_Department_vs_Incident_Status: "investigator_department_status",
  Continuer_Continuity_Test_Status: "continuer_status",
  Mitigator_Decision_Status_vs_Risk_Status: "mitigator_decision_status",
  Mitigator_Department_vs_Severity_Status:
    "mitigator_department_wise_severity_status",
  Mitigator_Status_vs_Severity_Status: "mitigator_status_wise_severity_status",
  Mitigator_Department_vs_Status_Status:
    "mitigator_department_wise_status_status",
};

const getModuleFromId = (moduleId) => {
  let moduleName;
  Object.keys(moduleIDs).forEach((key) => {
    if (moduleId.includes(moduleIDs[key])) {
      moduleName = key;
    }
  });
  return moduleName;
};

const PARENTS = [
  "remediator",
  "auditor",
  "documenter",
  "continuer",
  "educator",
  "investigator",
  "mitigator",
];
const ALIAS_MAP = {
  audit: "auditor",
  audits: "auditor",
  remediation: "remediator",
  compliance: "remediator",
  document: "documenter",
  continuity: "continuer",
  education: "educator",
  incident: "investigator",
  risk: "mitigator",
};

// Works for both leaf keys like "Remediator_Department_vs_..." and parent ids like "remediator"
const parentModuleForKey = (k = "") => {
  const lk = String(k).toLowerCase();
  // Direct prefix match
  for (const p of PARENTS) {
    if (lk === p || lk.startsWith(`${p}_`)) return p;
  }
  // Alias / fuzzy match
  for (const alias in ALIAS_MAP) {
    if (lk.includes(alias)) return ALIAS_MAP[alias];
  }
  //Fallback
  return "fusion";
};

// ---------- footer helpers ----------
function urlToDataURL(src) {
  return new Promise((resolve, reject) => {
    if (!src) return resolve(null);
    const img = new Image();
    img.crossOrigin = "anonymous";
    img.onload = () => {
      const c = document.createElement("canvas");
      c.width = img.naturalWidth;
      c.height = img.naturalHeight;
      const ctx = c.getContext("2d");
      ctx.drawImage(img, 0, 0);
      resolve(c.toDataURL("image/png"));
    };
    img.onerror = reject;
    img.src = src;
  });
}
async function ensureDataURL(src) {
  if (!src) return null;
  return src.startsWith("data:") ? src : await urlToDataURL(src);
}
async function dataURLToImage(dataUrl) {
  if (!dataUrl) return null;
  return await new Promise((resolve) => {
    const im = new Image();
    im.onload = () => resolve(im);
    im.src = dataUrl;
  });
}
function moduleKeyFromDomId(domId) {
  const base = domId.split("_export_")[0]; //remediator_department_wise_status
  return getModuleFromId(base); // Remediator_Department_vs_Compliance_Status
}
// --- helpers to compute scaled dimensions that preserve aspect ratio

async function resolveModuleLogo(domId, assets, cache) {
  const key = moduleKeyFromDomId(domId);
  if (!key) return null;
  if (cache[key]) return cache[key];
  const src = assets?.moduleLogosByKey?.[key];
  const data = await ensureDataURL(src);
  cache[key] = data;
  return data;
}

// --- helpers for footer ---------------------------------------------------------------
function pad2(n) {
  return String(n).padStart(2, "0");
}
function _coerceDate(val) {
  if (val instanceof Date) return val;
  if (typeof val === "number") return new Date(val);
  if (typeof val === "string") {
    const d = new Date(val);
    if (!isNaN(d)) return d;
  }
  return new Date();
}
function formatFooterDate(val) {
  const [dateStr, timeStr] = formatFooterDateLines(val);
  return `${dateStr} ${timeStr}`;
}

function formatFooterDateLines(val) {
  const d = _coerceDate(val);
  const dows = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];
  const mos = [
    "Jan",
    "Feb",
    "Mar",
    "Apr",
    "May",
    "Jun",
    "Jul",
    "Aug",
    "Sep",
    "Oct",
    "Nov",
    "Dec",
  ];
  const date = `${dows[d.getDay()]}, ${pad2(d.getDate())}-${
    mos[d.getMonth()]
  }-${d.getFullYear()}`;
  const time = `${pad2(d.getHours())}:${pad2(d.getMinutes())}`;
  return [date, time];
}
function _fitImagePdf(pdf, imgData, maxW, maxH) {
  const p = pdf.getImageProperties(imgData);
  const s = Math.min(maxW / p.width, maxH / p.height);
  return { w: p.width * s, h: p.height * s };
}
function drawImageFit(ctx, img, x, y, maxW, maxH) {
  const w = img.naturalWidth || img.width,
    h = img.naturalHeight || img.height;
  const s = Math.min(maxW / w, maxH / h);
  const dw = w * s,
    dh = h * s;
  // ctx.drawImage(img, x, y, dw, dh);
  return { w: dw, h: dh };
}

// --- MAIN: PDF footer ------------------------------------------------------
async function drawPdfFooter(
  pdf,
  {
    pageIndex = 0,
    totalPages = 1,
    leftLogo,
    rightLogo,
    footerHeight = 70,
    classification = "Restricted",
    generatedOn = new Date(),
  } = {},
) {
  await ensureExo2(pdf);

  const pageW = pdf.internal.pageSize.getWidth();
  const pageH = pdf.internal.pageSize.getHeight();
  const topY = pageH - footerHeight;
  const marginX = 15;
  const cx = pageW / 2;

  // separator (same color as Canvas)
  pdf.setDrawColor(33, 130, 130);
  pdf.setLineWidth(0.7);
  pdf.line(marginX - 12, topY + 2, pageW - (marginX - 12), topY + 2);

  // --- Logos (baseline aligned with independent sizing) ---
  const LEFT_MAX_H = 26,
    LEFT_MAX_W = 150; // bigger left logo
  const RIGHT_MAX_H = 15,
    RIGHT_MAX_W = 90; // smaller right logo
  const logoBaselineY = topY + 52;

  let leftW = 0,
    leftH = 0,
    rightW = 0,
    rightH = 0;

  if (leftLogo) {
    const { w, h } = _fitImagePdf(pdf, leftLogo, LEFT_MAX_W, LEFT_MAX_H);
    leftW = w;
    leftH = h;
    pdf.addImage(leftLogo, "PNG", marginX, logoBaselineY - h, w, h);
  }
  if (rightLogo) {
    const { w, h } = _fitImagePdf(pdf, rightLogo, RIGHT_MAX_W, RIGHT_MAX_H);
    rightW = w;
    rightH = h;
    pdf.addImage(
      rightLogo,
      "PNG",
      pageW - marginX - w,
      logoBaselineY - h,
      w,
      h,
    );
  }

  // ---------- Generated on (Canvas ~12px) ----------
  pdf.setFont("Exo2", "normal");
  pdf.setTextColor(107, 104, 104);
  pdf.setFontSize(6.5);
  const rightX = pageW - marginX;
  const [dateStr, timeStr] = formatFooterDateLines(generatedOn);
  pdf.text("Generated on:", rightX, topY + 20, { align: "right" });
  pdf.text(`${dateStr} ${timeStr}`, rightX, topY + 26, { align: "right" });

  // ---------- Page number (Canvas 16px; looks good around 10–11pt in PDF) ----------
  pdf.setFontSize(7.5);

  const pgPrefix = "- Page ";
  const pgOf = " of ";
  const pgSuffix = " -";
  const nStr = String(pageIndex + 1).padStart(2, "0");
  const tStr = String(totalPages).padStart(2, "0");
  pdf.setTextColor(0, 0, 0);
  pdf.setFont("Exo2", "normal");
  const prefixW = pdf.getTextWidth(pgPrefix);
  const ofW = pdf.getTextWidth(pgOf + tStr + pgSuffix);

  pdf.setFont("Exo2", "bold");
  const numW = pdf.getTextWidth(nStr);

  let x = cx - (prefixW + numW + ofW) / 2;
  const y = topY + 16;

  pdf.setFont("Exo2", "normal");
  pdf.text(pgPrefix, x, y);
  x += prefixW;
  pdf.setFont("Exo2", "bold");
  pdf.text(nStr, x, y);
  x += numW;
  pdf.setFont("Exo2", "normal");
  pdf.text(pgOf + tStr + pgSuffix, x, y);

  // ---------- Classification + pill (match Canvas look) ----------
  const label = "Classification:";
  const labelColor = [131, 128, 128]; // #838080
  const pillColor = [242, 96, 39]; // rgb(242,96,39)
  const labelSize = 7.5; // ~12px
  const pillTextSize = 9.5; // a bit bigger/bolder than label, like Canvas 16px

  // layout: label baseline at midY, pill centered vertically at same midY
  const midY = topY + 30;

  pdf.setFont("Exo2", "normal");
  pdf.setFontSize(labelSize);
  pdf.setTextColor(...labelColor);
  const labelW = pdf.getTextWidth(label);

  // measure pill text in bold
  pdf.setFont("Exo2", "normal");
  pdf.setFontSize(pillTextSize);
  const pillPadX = 3.5;
  const pillTextW = pdf.getTextWidth(classification);
  const pillW = pillTextW + pillPadX * 2;
  const pillH = 9.5; // slightly taller to feel like the Canvas pill

  const gap = 6; // spacing between label and pill
  const totalW = labelW + gap + pillW;
  const startX = cx - totalW / 2;

  // draw label (baseline aligned)
  pdf.setFont("Exo2", "normal");
  pdf.setFontSize(labelSize);
  pdf.setTextColor(...labelColor);
  pdf.text(label, startX, midY);

  // draw pill rectangle centered on midY
  const pillX = startX + labelW + gap;
  const pillY = midY - pillH * 0.75; // tweak so text sits optically centered
  pdf.setFillColor(...pillColor);
  pdf.rect(pillX, pillY, pillW, pillH, "F");

  // pill text centered
  const baselineTweak = pillTextSize * 0.35;
  pdf.setFont("Exo2", "normal"); // keep it less bold
  pdf.setFontSize(pillTextSize);
  pdf.setTextColor(255, 255, 255);
  pdf.text(
    classification,
    pillX + pillW / 2,
    pillY + pillH / 2 + baselineTweak - 1.0,
    {
      align: "center",
    },
  );

  // ---------- Disclaimer (centered between logos; left-justified lines) ----------
  const disc =
    "Controlled Master of this document exists within the Fusion system. Printed copies, standalone versions in PDF or other file formats & copies outside this network are considered 'Uncontrolled' & may not be up-to-date. Uncontrolled copies of this document SHOULD NOT be distributed freely. Obtain explicit permission from an Authorised Approver before sharing further.";

  pdf.setFont("Exo2", "normal");
  pdf.setFontSize(6.5);
  pdf.setTextColor(164, 162, 162); // #A4A2A2

  // free window between logos (with gutters so text never touches the logos)
  const GUTTER = 12;
  const leftEdge = marginX + leftW + GUTTER;
  const rightEdge = pageW - marginX - rightW - GUTTER;
  const freeW = Math.max(0, rightEdge - leftEdge);

  // cap width so lines don't get too long; then center block within window
  const BLOCK_CAP = 520;
  const blockW = Math.min(BLOCK_CAP, freeW);
  const leftX = leftEdge + (freeW - blockW) / 2;

  // wrap to block width
  const wrapped = pdf.splitTextToSize(disc, blockW);

  // vertically center the paragraph around logos midline
  const lineH = 7.8; // good for 6.5pt in jsPDF
  const textH = wrapped.length * lineH;
  const tallestLogoH = Math.max(leftH || 0, rightH || 0);
  const logosMidY = logoBaselineY - tallestLogoH / 2;

  // guards: stay below page number/classification and above the logos baseline
  const topGuard = Math.max(
    topY + 36,
    /* after "Classification" row */ midY + 8,
  );
  const bottomGuard = logoBaselineY - 3;

  let yStart = logosMidY - textH / 2;
  if (yStart < topGuard) yStart = topGuard;
  if (yStart + textH > bottomGuard)
    yStart = Math.max(topGuard, bottomGuard - textH);

  // draw the lines, left-aligned inside the centered block
  pdf.text(wrapped, leftX, yStart, { align: "left" });
}

// --- MAIN: Canvas footer ---------------------------------------------------
async function drawCanvasFooter(
  ctx,
  {
    canvasWidth,
    footerHeight = 90,
    leftLogoImg,
    rightLogoImg,
    pageIndex = 0,
    totalPages = 1,
    classification = "Restricted",
    generatedOn = new Date(),
  } = {},
) {
  const W = canvasWidth;
  const H = footerHeight;
  const startY = ctx.canvas.height - H;
  const marginX = 32;

  ctx.save();
  ctx.strokeStyle = "rgb(33,130,130)";
  ctx.lineWidth = 1;
  ctx.beginPath();
  ctx.moveTo(marginX - 12, startY + 2);
  ctx.lineTo(W - (marginX - 12), startY + 2);
  ctx.stroke();

  // ---------- logo sizing ----------
  const LEFT_LOGO_MAX_H = 50;
  const RIGHT_LOGO_MAX_H = 28;
  const baselineY = startY + H - 50;

  let leftW = 0,
    rightW = 0,
    sized;

  if (leftLogoImg) {
    sized = drawImageFit(ctx, leftLogoImg, 0, 0, 250, LEFT_LOGO_MAX_H);
    leftW = sized.w;
    ctx.drawImage(leftLogoImg, marginX, baselineY - sized.h, sized.w, sized.h);
  }

  if (rightLogoImg) {
    sized = drawImageFit(ctx, rightLogoImg, 0, 0, 180, RIGHT_LOGO_MAX_H);
    rightW = sized.w;
    ctx.drawImage(
      rightLogoImg,
      W - marginX - sized.w,
      baselineY - sized.h,
      sized.w,
      sized.h,
    );
  }

  // ---------- generated on ----------
  const [dateStr, timeStr] = formatFooterDateLines(generatedOn);
  ctx.fillStyle = "#6b6868ff";
  ctx.textAlign = "right";
  ctx.font = "12px 'Exo 2'";
  ctx.fillText("Generated on:", W - marginX, startY + 18);
  ctx.fillText(`${dateStr} ${timeStr}`, W - marginX, startY + 32);

  // ---------- page number ----------
  const cx = W / 2;
  ctx.textAlign = "center";
  ctx.fillStyle = "#000";
  ctx.font = "16px 'Exo 2'";
  const nStr = String(pageIndex + 1).padStart(2, "0");
  const tStr = String(totalPages).padStart(2, "0");
  const prefix = "- Page ";
  const ofStr = ` of ${tStr} -`;
  const preW = ctx.measureText(prefix).width;
  const numW = ctx.measureText(nStr).width;
  const allW = preW + numW + ctx.measureText(ofStr).width;
  let x = cx - allW / 2;
  ctx.textAlign = "left";
  ctx.font = "16px 'Exo 2'";
  ctx.fillText(prefix, x, startY + 22); // ⬅ a bit lower
  x += preW;
  ctx.font = "bold 16px 'Exo 2'";
  ctx.fillText(nStr, x, startY + 22);
  x += numW;
  ctx.font = "16px 'Exo 2'";
  ctx.fillText(ofStr, x, startY + 22);
  // ctx.fillText(`- Page ${nStr} of ${tStr} -`, cx, startY + 22);

  // ---------- classification ----------
  const label = "Classification:";
  ctx.font = "12px 'Exo 2'";
  ctx.fillStyle = "#838080";
  const labelW = ctx.measureText(label).width;
  const text = classification;
  const classPadX = 10;
  const classH = 22;
  const textW = ctx.measureText(text).width;
  const classW = textW + classPadX * 2;

  const totalW = labelW + 12 + classW;
  const startX = cx - totalW / 2;
  const midY = startY + 48; // aligned center line

  // label
  ctx.textAlign = "left";
  ctx.fillStyle = "#838080";
  ctx.fillText(label, startX, midY + 4);

  // pill
  const rectX = startX + labelW + 12;
  ctx.fillStyle = "rgb(242,96,39)";
  ctx.fillRect(rectX, midY - classH / 2, classW, classH);

  // pill text
  ctx.fillStyle = "#fff";
  ctx.font = "16px 'Exo 2'";
  ctx.textAlign = "center";
  ctx.textBaseline = "middle";
  ctx.fillText(text, rectX + classW / 2, midY);

  // ---------- disclaimer ----------
  const leftEdge = marginX + leftW + 10;
  const disc =
    "Controlled Master of this document exists within the Fusion system. Printed copies, standalone versions in PDF or other file formats & copies outside this network are considered 'Uncontrolled' & may not be up-to-date. Uncontrolled copies of this document SHOULD NOT be distributed freely. Obtain explicit permission from an Authorised Approver before sharing further.";

  ctx.fillStyle = "rgba(164,162,162,1)";
  ctx.font = "12px 'Exo 2'";
  ctx.textAlign = "left";

  // break disclaimer into multiple lines (left aligned)
  const maxW = W - leftEdge - rightW - 40;
  const words = disc.split(" ");
  const lines = [];
  let line = "";
  for (const w of words) {
    const t = line ? line + " " + w : w;
    if (ctx.measureText(t).width <= maxW) line = t;
    else {
      lines.push(line);
      line = w;
    }
  }
  if (line) lines.push(line);

  let y = midY + classH / 2 + 20;
  for (const l of lines) {
    ctx.fillText(l, leftEdge, y);
    y += 16;
  }

  ctx.restore();
}

async function drawPdfHeader(
  pdf,
  {
    moduleKey = "fusion",
    isFusionOnly = false,
    rightLogoDataURL = null, // <-- Org logo (base64)
    headerHeight = 60,
    margin = 15,
  } = {},
) {
  await ensureExo2(pdf);

  const pageW = pdf.internal.pageSize.getWidth();
  const topY = margin;

  // --- Title (Exo2) ---
  const LABELS = {
    remediator: "Compliance",
    auditor: "Audit",
    documenter: "Documentation",
    continuer: "Continuity",
    educator: "Education",
    investigator: "Incident",
    mitigator: "Risk",
    fusion: "Fusion",
  };
  const title = isFusionOnly
    ? "Fusion"
    : LABELS[String(moduleKey).toLowerCase()] || "Fusion";

  pdf.setFont("Exo2", "bold");
  pdf.setTextColor(35, 135, 135); // #238787
  pdf.setFontSize(16);
  const titleX = margin;
  const titleY = topY + 20;
  pdf.text(title, titleX, titleY);

  // underline only under the title (no full-width line)
  const tW = pdf.getTextWidth(title);
  pdf.setDrawColor(35, 135, 135);
  pdf.setLineWidth(1);
  pdf.line(titleX, titleY + 4, titleX + tW, titleY + 4);

  // --- Right: Org logo (smaller) ---
  let logoBottom = 0;

  if (rightLogoDataURL) {
    const MAX_W = 120,
      MAX_H = 24; // smaller than before
    const p = pdf.getImageProperties(rightLogoDataURL);
    const s = Math.min(MAX_W / p.width, MAX_H / p.height);
    const w = p.width * s,
      h = p.height * s;
    pdf.addImage(
      rightLogoDataURL,
      "PNG",
      pageW - margin - w,
      topY + 6, // nice visual alignment with title
      w,
      h,
    );
    logoBottom = topY + 6 + h;
  }

  // return reserved header space
  // return headerHeight;
  const underlineBottom = titleY + 3;

  const used = Math.ceil(
    Math.max(underlineBottom, logoBottom || underlineBottom) - topY + 4,
  );
  // return this smaller value
  return used;
}
// --- HEADER (Canvas) -------------------------------------------------------
async function drawCanvasHeader(
  ctx,
  {
    moduleKey = "fusion",
    isFusionOnly = false,
    rightLogoImg = null, // HTMLImageElement (org logo)
    headerHeight = 100,
    margin = 24,
  } = {},
) {
  // titles
  const LABELS = {
    remediator: "Compliance",
    auditor: "Audit",
    documenter: "Documentation",
    continuer: "Continuity",
    educator: "Education",
    investigator: "Incident",
    mitigator: "Risk",
    fusion: "Fusion",
  };
  const title = isFusionOnly
    ? "Fusion"
    : LABELS[String(moduleKey).toLowerCase()] || "Fusion";

  const pageW = ctx.canvas.width;
  const topY = margin;

  // title (Exo 2)
  ctx.fillStyle = "#238787";
  ctx.font = "bold 28px 'Exo 2'";
  ctx.textAlign = "left";
  ctx.textBaseline = "alphabetic";
  const titleX = margin;
  const titleY = topY + 30;
  ctx.fillText(title, titleX, titleY);

  // underline (just under title width)
  const tW = ctx.measureText(title).width;
  ctx.strokeStyle = "#238787";
  ctx.lineWidth = 3;
  ctx.beginPath();
  ctx.moveTo(titleX, titleY + 4);
  ctx.lineTo(titleX + tW, titleY + 4);
  ctx.stroke();

  // right logo (org)
  let logoBottom = 0;
  if (rightLogoImg) {
    const MAX_W = 190,
      MAX_H = 54;
    const w = rightLogoImg.naturalWidth || rightLogoImg.width;
    const h = rightLogoImg.naturalHeight || rightLogoImg.height;
    const s = Math.min(MAX_W / w, MAX_H / h);
    const dw = Math.round(w * s);
    const dh = Math.round(h * s);
    ctx.drawImage(rightLogoImg, pageW - margin - dw, topY + 6, dw, dh);
    logoBottom = topY + 6 + dh;
  }

  // return used height (like PDF)
  const underlineBottom = titleY + 6;
  const used = Math.ceil(
    Math.max(underlineBottom, logoBottom || underlineBottom) - topY + 6,
  );
  return Math.max(used, headerHeight);
}

//waiting for component to finish rendering before exporting
const sleep = (ms) => new Promise((r) => setTimeout(r, ms));

function _isRenderable(node) {
  if (!node) return false;
  const r = node.getBoundingClientRect();
  return r.width > 1 && r.height > 1 && node.offsetParent !== null;
}

function _isTableReady(domId) {
  const meta = getExportMeta(domId);
  const rows = meta?.data?.rows;
  return Array.isArray(rows) && rows.length > 0;
}

function _isChartReady(node) {
  const c = node?.querySelector?.("canvas");
  return !!(c && c.width > 1 && c.height > 1);
}

/** Wait until section is visibly sized and (if possible) has data/canvas */
async function _waitSectionReady(domId, { timeout = 500, step = 120 } = {}) {
  const start = performance.now();
  while (performance.now() - start < timeout) {
    const el = document.getElementById(domId);
    const meta = getExportMeta(domId);

    if (_isRenderable(el)) {
      if (meta?.type === "table" && _isTableReady(domId)) return true;
      if (meta?.type === "chart" && _isChartReady(el)) return true;
      if (!meta?.type) return true; // unknown type: at least sized
    }
    window.dispatchEvent(new Event("resize")); // nudge layout-based charts
    await sleep(step);
  }
  // Timed out: proceed (might be legit "no data")
  return false;
}

function svgToDataUrl(svg) {
  const s = new XMLSerializer().serializeToString(svg);
  return "data:image/svg+xml;charset=utf-8," + encodeURIComponent(s);
}

async function getChartSnapshots(chartIds = []) {
  const out = {};
  for (const id of chartIds) {
    const host = document.getElementById(id);
    if (!host) continue;

    // Prefer <canvas>
    const canvas = host.querySelector("canvas");
    if (canvas?.toDataURL) {
      out[id] = canvas.toDataURL("image/png");
      continue;
    }

    // Fallback: inline <svg>
    const svg = host.querySelector("svg");
    if (svg) {
      out[id] = svgToDataUrl(svg);
      continue;
    }
  }
  return out;
}

async function renderSectionToImage(
  node,
  { margin = 90, footerHeight = 70, chartIds = [] } = {},
) {
  // 1) snapshot current charts from the LIVE DOM (does not touch UI)
  const chartImgs = await getChartSnapshots(chartIds);

  // 2) clone + render, swapping donuts with <img> only in the clone
  const canvas = await html2canvas(node, {
    scrollY: -window.scrollY,
    useCORS: true,
    letterRendering: 1,
    scale: Math.max(2, window.devicePixelRatio || 1),
    backgroundColor: "#fff",
    onclone: (doc) => {
      // export-only CSS (see next section)
      const style = doc.createElement("style");
      // style.textContent = `.export-mode *{transform:none!important;filter:none!important}`;
      style.textContent = `
        /* existing export safety */
        .export-mode * { transform: none !important; filter: none !important; }

        /* 🔓 turn off ellipsis ONLY in exported clone */
        .export-mode .nm-stats-cell .cell-ellipsis {
          white-space: normal !important;   /* allow wrapping */
          overflow: visible !important;     /* don’t clip */
          text-overflow: clip !important;   /* no … */
          max-width: none !important;       /* don’t constrain width inside cell */
          display: inline !important;       /* let text wrap normally */
        }
      `;
      doc.head.appendChild(style);
      doc.body.classList.add("export-mode");

      for (const id of chartIds) {
        const host = doc.getElementById(id);
        const url = chartImgs[id];
        if (!host || !url) continue;

        host.innerHTML = "";
        const img = doc.createElement("img");
        img.src = url;
        img.style.width = "100%";
        img.style.height = "100%";
        img.style.objectFit = "contain";
        host.appendChild(img);
      }
    },
  });

  if (!canvas?.width || !canvas?.height) return null;

  const newCanvas = document.createElement("canvas");
  newCanvas.width = canvas.width + 2 * margin;
  newCanvas.height = canvas.height + 2 * margin + footerHeight;

  const ctx = newCanvas.getContext("2d");
  ctx.fillStyle = "white";
  ctx.fillRect(0, 0, newCanvas.width, newCanvas.height);
  ctx.drawImage(canvas, margin, margin);

  return {
    imgData: newCanvas.toDataURL("image/jpeg"),
    width: newCanvas.width,
    height: newCanvas.height,
  };
}

// ---------------- core util ----------------
const generateIDsToExport = (modules, opts = {}) => {
  const { filterByType, includeIndices } = opts; // includeIndices: Set<number>
  const idsToExport = [];
  for (const module of modules) {
    const base = moduleIDs[module];
    if (!base) continue;
    let i = 1;
    // probe sequentially: <base>_export_1, _2, ...
    // stop when miss
    while (true) {
      const domId = `${base}_export_${i}`;
      const el = document.getElementById(domId);
      if (!el) break;

      if (includeIndices && !includeIndices.has(i)) {
        i += 1;
        continue;
      }
      if (filterByType) {
        const meta = exportRegistry.get(domId);
        if (!meta || meta.type !== filterByType) {
          i += 1;
          continue;
        }
      }
      idsToExport.push(domId);
      i += 1;
    }
  }
  return idsToExport;
};

// ---------------- PDF ----------------
export const exportGroupToPDF = async (
  selectedModules,
  opts = {},
  assets = {},
) => {
  exo2Ready = false;
  // expand "Export All"
  let modulesList = selectedModules.includes("Export All")
    ? [
        "Documenter_Documentation_Status",
        "Remediator_Department_vs_Compliance_Status",
        "Remediator_Priority_vs_Compliance_Status",
        "Remediator_Department_vs_Priority_Status",
        "Auditor_Audit_Status",
        "Auditor_overview",
        "Educator_Assessment_vs_Status",
        "Educator_Department_vs_Status",
        "Educator_Overall_Coverage",
        "Investigator_Severity_vs_Incident_Status",
        "Investigator_Department_vs_Incident_Status",
        "Continuer_Continuity_Test_Status",
      ]
    : selectedModules;

  const ids = generateIDsToExport(modulesList, opts);
  if (!ids.length) {
    console.warn(
      "No exportable sections found for PDF.",
      selectedModules,
      opts,
    );
    return;
  }

  // show loader (guard)
  const loadingEl = document.getElementById("loader-component");
  if (loadingEl) loadingEl.style.display = "block";

  // show sections
  const elements = [];
  for (const id of ids) {
    const el = document.getElementById(id);
    if (el) {
      el.dataset._origDisplay = el.style.display || "";
      el.style.display = "block";
      elements.push(el);
    }
  }
  // footer assets
  const fusionLogoData = await ensureDataURL(assets?.neumetricLogo);
  const moduleLogoCache = {}; // moduleKey -> dataURL

  const pdf = new jsPDF("l", "px", "a4");
  const FOOTER_H = 50;
  // set one margin and reuse for both directions
  const PAGE_MARGIN = 15; // ← pick what you want; this will be top/left/right
  const HEADER_H = 60;
  for (let i = 0; i < elements.length; i++) {
    const el = elements[i];
    const isLast = i === elements.length - 1;
    const domId = ids[i];

    // Wait until the section has data/size (or timeout)
    await _waitSectionReady(domId);
    // --- TEMP FIX (only for Remediator donuts) ---
    let appliedRemediatorFix = false;
    const isRemediatorSection =
      el.querySelector(".remediation-donut") ||
      el.querySelector("#remediator-overall-donut") ||
      el.querySelector("#remediator-priority-donut");

    if (isRemediatorSection) {
      appliedRemediatorFix = true;
      const donutDiv = el.querySelector(".remediation-donut") || el;
      // Backup original inline styles so we can restore later
      donutDiv.dataset._origHeight = donutDiv.style.height;
      donutDiv.dataset._origPaddingTop = donutDiv.style.paddingTop;
      donutDiv.dataset._origPaddingBottom = donutDiv.style.paddingBottom;

      // Apply export-only adjustments
      donutDiv.style.height = "240px";
      donutDiv.style.paddingTop = "10px";
      donutDiv.style.paddingBottom = "10px";

      await new Promise((r) => requestAnimationFrame(r)); // ensure reflow
    }

    const parentModule = parentModuleForKey(domId) || "fusion";

    const imgObj = await renderSectionToImage(el, {
      pad: { top: 6, right: 6, bottom: 24, left: 6 },
      chartIds: [
        "documenter-overall-donut",
        "remediator-overall-donut",
        "remediator-priority-donut",
        "continuer-overall-donut",
        "investigator-dept-donut",
        "investigator-severity-donut",
      ],
    });

    if (!imgObj) continue;
    const { imgData, width, height } = imgObj;
    const pageW = pdf.internal.pageSize.getWidth();
    const pageH = pdf.internal.pageSize.getHeight();
    const moduleLogoData = await resolveModuleLogo(
      domId,
      assets,
      moduleLogoCache,
    );
    // const headerUsed = await drawPdfHeader(pdf, {
    //   moduleKey: parentModule,
    //   isFusionOnly: opts?.isExportFusionOnly,
    //   rightLogoDataURL: orgLogoDataURL, // prefer Org; fallback Neumetric
    //   headerHeight: 60,
    //   margin: PAGE_MARGIN,
    // });
    const headerUsed = 60;
    // compute the content box BETWEEN header and footer, inset by PAGE_MARGIN
    const contentW = pageW - 2 * PAGE_MARGIN;
    const contentH = pageH - headerUsed - FOOTER_H - 2 * PAGE_MARGIN;

    const scale = Math.min(contentW / width, contentH / height);
    const drawW = Math.floor(width * scale);
    const drawH = Math.floor(height * scale);

    const x = PAGE_MARGIN;
    const y = PAGE_MARGIN + headerUsed; // start under header
    // const y = Math.max(PAGE_MARGIN, PAGE_MARGIN + headerUsed - 6);

    pdf.addImage(imgData, "PNG", x, y, drawW, drawH);

    await drawPdfHeader(pdf, {
      moduleKey: parentModule,
      isFusionOnly: opts?.isExportFusionOnly,
      rightLogoDataURL: orgLogoDataURL,
      headerHeight: HEADER_H,
      margin: PAGE_MARGIN,
    });

    await drawPdfFooter(pdf, {
      pageIndex: i,
      totalPages: ids.length,
      leftLogo: moduleLogoData,
      rightLogo: fusionLogoData,
      footerHeight: 90,
      classification: "Restricted",
      generatedOn: formatFooterDate(), // optional
    });
    if (!isLast) pdf.addPage();
  }

  // ---------------- filename ----------------
  const passedName =
    opts && typeof opts.pdfFileName === "string" && opts.pdfFileName.trim()
      ? opts.pdfFileName.trim()
      : null;

  const pdfSaveName = passedName || "Fusion_Central";
  pdf.save(`${pdfSaveName}.pdf`);

  // hide loader
  setTimeout(() => {
    if (loadingEl) loadingEl.style.display = "none";
  }, 1000);

  // restore display
  elements.forEach((el) => (el.style.display = el.dataset._origDisplay || ""));
  // --- IMPORTANT: allow next export to load fonts again into its new jsPDF ---
  exo2Ready = false;
};

// ---------------- PNG ----------------
//--------helpers----------//

const sanitizeFile = (s = "") =>
  (s || "")
    .replace(/[<>:"/\\|?*\u0000-\u001F]/g, "")
    .replace(/\s+/g, " ")
    .trim()
    .replace(/ /g, "_");

// e.g. "remediator_priority_wise_status_export_2" -> "remediator_priority_wise_status"
const baseFromDomId = (domId = "") => domId.split("_export_")[0] || "";

// Build a reverse lookup once: domBase -> canonicalKey
// Uses your provided mapping:
const REVERSE_MODULE_IDS = (() => {
  const out = Object.create(null);
  try {
    // moduleIDs must be imported/available in this file (it is in your PDF code)
    for (const [canonical, domBase] of Object.entries(moduleIDs || {})) {
      out[String(domBase)] = String(canonical);
    }
  } catch {}
  return out;
})();

// tolerant map lookup (handles stray leading/trailing spaces in keys)
const lookupLabel = (map, key) => {
  if (!map || !key) return undefined;
  return map[key] ?? map[key.trim()];
};

// FINAL: PNG Export Function (stable + fixed)
export const exportGroupToPNGInZip = async (
  selectedModules,
  opts = {},
  assets = {},
) => {
  const ts = formatYYMMDDHHMM?.() || getDateInExportFormat(new Date());

  const modulesList = selectedModules.includes("Export All")
    ? Object.keys(moduleIDs)
    : selectedModules;

  const ids = generateIDsToExport(modulesList, opts);
  if (!ids.length) {
    console.warn("No exportable sections found for PNG.");
    return;
  }

  const loadingEl = document.getElementById("loader-component");
  if (loadingEl) loadingEl.style.display = "block";

  const fusionLogoData = await ensureDataURL(assets?.neumetricLogo);
  const fusionImg = await dataURLToImage(fusionLogoData);
  const moduleLogoCache = {};

  const zip = new JSZip();
  const nameCounts = new Map();
  // ✅ define basePrefix ONCE outside the loop
  const basePrefix = `Fusion_Central_${ts}`;

  for (let idx = 0; idx < ids.length; idx++) {
    const domId = ids[idx];
    const el = document.getElementById(domId);
    if (!el) continue;

    // ---- Detect and temporarily adjust donut styles (like PDF) ----
    const isRemediatorSection =
      el.querySelector(".remediation-donut") ||
      el.querySelector("#remediator-overall-donut") ||
      el.querySelector("#remediator-priority-donut");

    let donutDiv = null;
    if (isRemediatorSection) {
      donutDiv = el.querySelector(".remediation-donut") || el;
      donutDiv.dataset._origHeight = donutDiv.style.height;
      donutDiv.dataset._origPaddingTop = donutDiv.style.paddingTop;
      donutDiv.dataset._origPaddingBottom = donutDiv.style.paddingBottom;

      donutDiv.style.height = "240px";
      donutDiv.style.paddingTop = "10px";
      donutDiv.style.paddingBottom = "10px";

      await new Promise((r) => requestAnimationFrame(r));
    }

    // --- isolate capture per section ---
    const clone = el.cloneNode(true);
    clone.style.display = "block";
    clone.style.position = "absolute";
    clone.style.top = "-9999px";
    clone.style.left = "-9999px";
    clone.style.width = el.offsetWidth + "px";
    clone.style.zIndex = "99999";
    document.body.appendChild(clone);

    const parentModule = parentModuleForKey(domId) || "fusion";

    //  Wait one frame to ensure DOM is painted
    await new Promise((r) => requestAnimationFrame(r));

    // use same logic as PDF for renderSectionToImage
    const imgObj = await renderSectionToImage(clone, {
      chartIds: [
        "documenter-overall-donut",
        "remediator-overall-donut",
        "remediator-priority-donut",
        "continuer-overall-donut",
        "investigator-dept-donut",
        "investigator-severity-donut",
      ],
    });

    document.body.removeChild(clone); // cleanup before next
    // restore donut styles
    if (donutDiv) {
      donutDiv.style.height = donutDiv.dataset._origHeight;
      donutDiv.style.paddingTop = donutDiv.dataset._origPaddingTop;
      donutDiv.style.paddingBottom = donutDiv.dataset._origPaddingBottom;
    }

    if (!imgObj || !imgObj.imgData) {
      console.warn("No image data for", domId);
      continue;
    }

    // const { imgData, width, height } = imgObj;
    // ---- A4 portrait fixed width + dynamic height ----

    // pick DPI once (150 DPI ≈ 1240 px wide A4)
    const PNG_DPI = 150;
    const mmToPx = (mm, dpi = PNG_DPI) => Math.round((mm / 25.4) * dpi);

    // A4 portrait 210 mm × 297 mm
    const A4_WIDTH = mmToPx(210); // ≈ 1240 px
    const PAGE_MARGIN = { top: 0, right: 24, bottom: 24, left: 24 };
    const FOOTER_H = 150;
    const HEADER_H = 80;

    // use actual section size
    const { imgData, width: srcW, height: srcH } = imgObj;

    // scale so the exported width = A4 width minus margins
    const contentW = A4_WIDTH - PAGE_MARGIN.left - PAGE_MARGIN.right;
    const scale = contentW / srcW;
    const scaledW = contentW;
    const scaledH = Math.round(srcH * scale);

    // total canvas height = scaled + margins + footer
    const outW = A4_WIDTH;
    const headerBudget = 100; // generous; actual used height is returned by drawCanvasHeader
    const outH =
      headerBudget + scaledH + PAGE_MARGIN.top + PAGE_MARGIN.bottom + FOOTER_H;

    // const outH = scaledH + PAGE_MARGIN.top + PAGE_MARGIN.bottom + FOOTER_H;

    const canvas = document.createElement("canvas");
    canvas.width = outW;
    canvas.height = outH;

    const ctx = canvas.getContext("2d");
    ctx.fillStyle = "#fff";
    ctx.fillRect(0, 0, outW, outH);
    const orgLogoImg = orgLogoDataURL
      ? await dataURLToImage(orgLogoDataURL)
      : null;
    //draw header
    const headerUsed = await drawCanvasHeader(ctx, {
      moduleKey: parentModule,
      isFusionOnly: opts?.isExportFusionOnly,
      rightLogoImg: orgLogoImg,
      headerHeight: HEADER_H,
      margin: PAGE_MARGIN.left,
    });

    // draw scaled section
    const image = await dataURLToImage(imgData);
    const sectionY = Math.max(
      PAGE_MARGIN.top,
      PAGE_MARGIN.top + headerUsed - 6,
    );
    ctx.drawImage(image, PAGE_MARGIN.left, sectionY, scaledW, scaledH);
    // ctx.drawImage(image, PAGE_MARGIN.left, PAGE_MARGIN.top, scaledW, scaledH);
    const moduleLogoData = await resolveModuleLogo(
      domId,
      assets,
      moduleLogoCache,
    );
    const moduleImg = await dataURLToImage(moduleLogoData);

    // draw footer normally
    await drawCanvasFooter(ctx, {
      canvasWidth: outW,
      footerHeight: FOOTER_H,
      leftLogoImg: moduleImg,
      rightLogoImg: fusionImg,
      pageIndex: idx,
      totalPages: ids.length,
      classification: "Restricted",
      generatedOn: new Date(),
    });

    const moduleLabel =
      opts?.parentLabelMap?.[parentModule] ||
      parentModule.charAt(0).toUpperCase() + parentModule.slice(1);

    const canonicalKey = REVERSE_MODULE_IDS[baseFromDomId(domId)];
    const statsTitle =
      (canonicalKey && lookupLabel(opts?.idToLabelMap, canonicalKey)) ||
      lookupLabel(opts?.idToLabelMap, baseFromDomId(domId)) ||
      "Overview";

    const fileBase = sanitizeFile(`${basePrefix}_${moduleLabel}_${statsTitle}`);
    const count = (nameCounts.get(fileBase) || 0) + 1;
    nameCounts.set(fileBase, count);
    const uniqueName = count === 1 ? fileBase : `${fileBase}_${count}`;

    zip.file(
      `${uniqueName}.png`,
      canvas.toDataURL("image/png").split("base64,")[1],
      { base64: true },
    );
  }

  const content = await zip.generateAsync({ type: "blob" });
  const link = document.createElement("a");
  link.href = URL.createObjectURL(content);
  link.download = `${basePrefix}.zip`;
  link.click();

  if (loadingEl) setTimeout(() => (loadingEl.style.display = "none"), 1000);
};

// ---------------- CSV helpers ----------------
const escapeCSV = (val) => {
  if (val == null) return "";
  const s = String(val).replace(/"/g, '""');
  return /[",\n]/.test(s) ? `"${s}"` : s;
};
const rowsToCSV = (columns = [], rows = []) => {
  const headers = columns.map((c) => c.title ?? c.attribute ?? "").join(",");
  const attrs = columns.map((c) => c.attribute);
  const body = rows.map((r) => attrs.map((a) => escapeCSV(r?.[a])).join(","));
  return [headers, ...body].join("\n");
};

export const exportGroupToCSV = async (selectedModules, opts = {}) => {
  const modules = generateIDsToExport(selectedModules, {
    ...opts,
    filterByType: "table",
  });
  if (!modules.length) {
    console.warn("No exportable table sections found for CSV.");
    return;
  }
  const ts = formatYYMMDDHHMM?.() || getDateInExportFormat(new Date());

  if (modules.length === 1) {
    const domId = modules[0];
    const meta = getExportMeta(domId);
    if (!meta?.data) return;
    const csv = rowsToCSV(meta.data.columns, meta.data.rows);
    // --- naming like PNG: Fusion_Central_YYMMDDHHMM_[Module]_[StatsChartsTitle].csv
    const base = baseFromDomId(domId); // e.g., remediator_priority_wise_status
    const parentKey = (base.split("_")[0] || "").toLowerCase();
    const moduleLabel =
      opts?.parentLabelMap?.[parentKey] ||
      (parentKey
        ? parentKey.charAt(0).toUpperCase() + parentKey.slice(1)
        : "Fusion");

    const canonicalKey = REVERSE_MODULE_IDS[base];
    const statsTitle =
      (canonicalKey && lookupLabel(opts?.idToLabelMap, canonicalKey)) ||
      lookupLabel(opts?.idToLabelMap, base) ||
      "Overview";

    const fileBase = sanitizeFile(
      `Fusion_Central_${ts}_${moduleLabel}_${statsTitle}`,
    );

    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const link = document.createElement("a");
    link.href = URL.createObjectURL(blob);
    // link.download = `Fusion_Stats_${exportDate}_${meta.baseId}_overview.csv`;
    link.download = `${fileBase}.csv`;
    link.click();
    return;
  }

  const zip = new JSZip();
  const nameCounts = new Map();
  const ensureUnique = (name) => {
    const n = (nameCounts.get(name) || 0) + 1;
    nameCounts.set(name, n);
    return n === 1 ? name : name.replace(/\.csv$/i, `(${n}).csv`);
  };
  modules.forEach((domId, idx) => {
    const meta = getExportMeta(domId);
    if (!meta?.data) return;
    const csv = rowsToCSV(meta.data.columns, meta.data.rows);
    // --- build per-file name same as PNG
    const base = baseFromDomId(domId);
    const parentKey = (base.split("_")[0] || "").toLowerCase();
    const moduleLabel =
      opts?.parentLabelMap?.[parentKey] ||
      (parentKey
        ? parentKey.charAt(0).toUpperCase() + parentKey.slice(1)
        : "Fusion");

    const canonicalKey = REVERSE_MODULE_IDS[base];
    const statsTitle =
      (canonicalKey && lookupLabel(opts?.idToLabelMap, canonicalKey)) ||
      lookupLabel(opts?.idToLabelMap, base) ||
      "Overview";

    const rawName = sanitizeFile(
      `Fusion_Central_${ts}_${moduleLabel}_${statsTitle}.csv`,
    );
    const uniqueName = ensureUnique(rawName);
    zip.file(uniqueName, csv);
  });
  const content = await zip.generateAsync({ type: "blob" });
  const link = document.createElement("a");
  link.href = URL.createObjectURL(content);
  // link.download = `Fusion_Stats_${exportDate}_overview_tables.zip`;
  link.download = `Fusion_Central_${ts}.zip`;
  link.click();
};
